import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings, Settings2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {useNavigate, useParams } from "react-router";
import MigrationStatusChip from "../components/common/MigrationStatusChip";
import { ProviderWithStatistics } from "../models/Provider";
import ProvidersRepository from "../repositories/ProvidersRepository";
import React from "react";
import { ProviderFile } from "../models/ProviderFile";
import {UserRole} from "../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import DeleteProviderFilesStatusTable from "../components/ProviderFiles/DeleteProviderFilesStatusTable";
import {ProviderFilesTable, ProviderFilesTableRef} from "../components/ProviderFiles/ProviderFilesTable";
import {ConflictResolveView} from "../components/common/Table/File/ConflictResolveView";
import {CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS, CreateDeleteFilesRequestTable} from "../components/common/File/CreateDeleteFilesRequestTable";
import {QUERY_KEYS} from "../constants/QueryKeys";
import { ProviderEmptyFoldersTable } from "../components/ProviderEmptyFolders/ProviderEmptyFoldersTable";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import { ExportProviderFilesToCsvStatusTable } from "../components/ProviderFiles/ExportProviderFilesToCsvStatusTable";
import {ActiveJobsExplorer} from "../models/Jobs/ActiveJobsExplorer";
import MigrationEntityShortStatistics from "../components/common/MigrationEntity/MigrationEntityShortStatistics";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import { useDetailBreadcrumb } from "../contexts/DetailBreadcrumbContext";
import { FlaggedPathResolveView } from "../components/common/Table/File/FlaggedPathResolveView";
import { MigrationType } from "../models/Statistics/MigrationType";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface ProviderPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ProviderPage: React.FC<ProviderPageProps> = ({
  userRole,
  isAssignedUser
}: ProviderPageProps) => {
  const requiresReason = useRequiresReason();
  const { organizationId, providerId } = useParams();
  const navigate = useNavigate();
  const providersRepository = new ProvidersRepository();
  const providerFilesTableRef = useRef<ProviderFilesTableRef>(null);
  const [providerEntity, setProvider] = useState<ProviderWithStatistics | undefined>(undefined);
  const [isConfirmExportModalOpen, setIsConfirmExportModalOpen] = React.useState<boolean>(false);
  const [newProviderFilesForDeletion, setNewProviderFilesForDeletion] = useState<ProviderFile[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveJobsExplorer>(ActiveJobsExplorer.Export);
  const [viewMode, setViewMode] = useState<"files" | "folders">("files");
  const queuedCount = newProviderFilesForDeletion.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);
  const { setEntityLabel } = useDetailBreadcrumb();

  const providerQuery = useQuery({ queryKey: [QUERY_KEYS.PROVIDER, providerId], queryFn:
    async () => await providersRepository.getProvider(providerId ?? ""), gcTime: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_CACHE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_REFETCH_INTERVAL,
      refetchOnMount: true, });

  useEffect(() => {
    if (providerQuery.data) {
      setProvider(providerQuery.data);
      setEntityLabel(providerQuery.data.providerNameId);
    }
    return () => setEntityLabel(null);
  }, [providerQuery.data, providerId]);

  if (providerQuery.isLoading || (!providerQuery.isError && !providerEntity))
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    );

  if (providerQuery.isError)
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Failed to load provider.</p>
        <Button variant="outline" size="sm" onClick={() => providerQuery.refetch()}>
          Retry
        </Button>
      </div>
    );

  if (!providerEntity) return null;

  const startCurrentViewExportToCsv = (reason?: string) =>
    providerFilesTableRef.current?.exportCurrentViewToCsv(reason);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="w-full my-2 gap-2 flex-wrap shrink-0">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-start">
            <h2 className="text-3xl font-bold tracking-tight">
              Provider {providerEntity.providerNameId}{" "}
              <MigrationStatusChip status={providerEntity.status} />
            </h2>
            <div className="flex flex-row gap-2">
              {userRole !== UserRole.Viewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  className="gap-2"
                >
                  <Settings2 className="size-4" />
                  Manage
                  {queuedCount > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5">
                      {queuedCount}
                    </Badge>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/organization/${organizationId}/providers`)}
              >
                Back
              </Button>
            </div>
          </div>
          <div className="flex flex-row w-full flex-1 justify-between items-center gap-2">
            <MigrationEntityShortStatistics
              totalNumberOfFiles={providerEntity.statistics.totalNumberOfFiles ?? 0}
              filesMigrated={providerEntity.statistics.filesMigrated ?? 0}
              filesFlagged={providerEntity.statistics.filesFlagged ?? 0}
              filesBlockedByEntityFlag={providerEntity.statistics.filesInEntityBlockedByUser ?? 0}
              filesInterruptedInitial={providerEntity.statistics.filesInterruptedInitial ?? 0}
              filesInterruptedDelta={providerEntity.statistics.filesInterruptedDelta ?? 0}
              filesModified={providerEntity.statistics.filesModified ?? 0}
              filesNotFound={providerEntity.statistics.filesNotFound ?? 0}
              filesNotFoundMarkedForRetry={providerEntity.statistics.filesNotFoundMarkedForRetry ?? 0}
              unresolvedConflictCount={providerEntity.statistics.unresolvedConflictCount ?? 0}
              acknowledgedConflictCount={providerEntity.statistics.acknowledgedConflictCount ?? 0}
              filesSizeInMb={providerEntity.statistics.filesSizeInMb ?? 0}
              filesSkippedDuplicate={providerEntity.statistics.duplicateFilesSkipped ?? 0}
            />
            <div className="flex flex-row gap-2 items-center">
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          {viewMode === "files" ? (
            <ProviderFilesTable
              ref={providerFilesTableRef}
              organizationId={organizationId ?? ""}
              providerId={providerId ?? ""}
              activeJobsExplorer={selectedTab}
              newProviderFilesForDeletion={newProviderFilesForDeletion}
              setNewProvidersFilesForDeletion={setNewProviderFilesForDeletion}
              disabled={userRole === UserRole.Viewer}
              toolbar={
                <div className="flex w-full items-center gap-2 my-1">
                  <ToggleGroup type="single" value={viewMode} onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }} variant="outline" size="sm" className="flex-1">
                    <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                    <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                  </ToggleGroup>
                  {providerEntity.hasFlaggedPaths && (
                    <FlaggedPathResolveView
                      organizationId={organizationId ?? ""}
                      migrationEntityId={providerEntity.providerNameId}
                      migrationType={MigrationType.ProvidersMigration}
                      entityNoun="provider"
                      invalidateQueryKeys={[[QUERY_KEYS.PROVIDER]]}
                    />
                  )}
                  {userRole !== UserRole.Viewer && (
                    <ConflictResolveView
                      parentId={providerId ?? ""}
                      entityNoun="provider"
                      groupsQueryKey={[QUERY_KEYS.PROVIDER_CONFLICT_GROUPS, providerId]}
                      invalidateQueryKeys={[[QUERY_KEYS.PROVIDER_FILES], [QUERY_KEYS.PROVIDER, providerId]]}
                      fetchGroups={(id, includeResolved) => providersRepository.getConflictGroups(id, includeResolved)}
                      resolveGroup={(resolutions) => providersRepository.resolveConflictGroup(resolutions)}
                    />
                  )}
                </div>
              }
            />
          ) : (
            <ProviderEmptyFoldersTable
              providerId={providerId ?? ""}
              toolbar={
                <ToggleGroup type="single" value={viewMode} onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }} variant="outline" size="sm" className="w-full my-1">
                  <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                  <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                </ToggleGroup>
              }
            />
          )}
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage files</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveJobsExplorer.Delete)} className="flex-1">Delete files</ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveJobsExplorer.Export)} className="flex-1">Export CSV</ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 overflow-auto min-h-0">
                {selectedTab === ActiveJobsExplorer.Delete && (userRole === UserRole.Admin || isAssignedUser) && (
                  <div className="flex flex-col h-full gap-4">
                    {newProviderFilesForDeletion.length > 0 && (
                      <>
                        <div className="flex items-center gap-3 py-2 pt-4">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                          <Separator className="flex-1" />
                        </div>
                        <CreateDeleteFilesRequestTable
                          organizationId={organizationId ?? ""}
                          entityId={providerId ?? ""}
                          entityKey={providerEntity.providerNameId}
                          newFilesForDeletion={newProviderFilesForDeletion}
                          setNewFilesForDeletion={setNewProviderFilesForDeletion}
                          fileTypeConfig={CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS.PROVIDER_FILES_CONFIG}
                        />
                        <div className="flex items-center gap-3 py-2">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>Deletion status</b></span>
                          <Separator className="flex-1" />
                        </div>
                      </>
                    )}
                    <DeleteProviderFilesStatusTable providerId={providerId ?? ""} />
                  </div>
                )}
                {selectedTab === ActiveJobsExplorer.Export && (
                  <div className="flex flex-col h-full gap-4">
                    <div className="pt-4 w-full flex-1">
                      {requiresReason ? (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.EXPORT_PROVIDER_FILES_VIEW}
                          onConfirm={async (reason) => {
                            startCurrentViewExportToCsv(reason);
                          }}
                        >
                          <Button>Export current files view</Button>
                        </ActionConfirmationModal>
                      ) : (
                        <Button onClick={() => setIsConfirmExportModalOpen(true)}>
                          Export current files view
                        </Button>
                      )}
                      <ExportProviderFilesToCsvStatusTable
                        providerId={providerId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                        organizationId={organizationId ?? ""}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {isConfirmExportModalOpen && (
        <ConfirmationModal
          open={isConfirmExportModalOpen}
          onClose={() => {setIsConfirmExportModalOpen(false);}}
          onConfirm={async () => {
            startCurrentViewExportToCsv();
            setIsConfirmExportModalOpen(false);
          }}
          message="Are you sure you want to export provider files from the current view?"
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};

export default ProviderPage;
